/**
 * Presets queries and mutations
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreatePresetPayload,
  Preset,
  UpdatePresetPayload,
} from '@/features/smswiser-campaigns/components/PresetsModal/shared';
import api from '@/services/api';

import { keys } from '../keys';
import { unwrapApiData } from '../utils';

export function usePresets(countryKey: string) {
  return useQuery({
    queryKey: keys.presets(countryKey),
    queryFn: async () => {
      const res = await api.get(`/smswiser/countries/${encodeURIComponent(countryKey)}/presets`);
      return unwrapApiData<Preset[]>(res.data);
    },
    enabled: Boolean(countryKey),
    staleTime: 60_000,
  });
}

export function useCreatePreset(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePresetPayload) => {
      const res = await api.post(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/presets`,
        payload,
      );
      return unwrapApiData<Preset>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useUpdatePreset(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & UpdatePresetPayload) => {
      const res = await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/presets/${id}`,
        payload,
      );
      return unwrapApiData<Preset>(res.data);
    },
    onSuccess: (updatedPreset) => {
      qc.setQueryData(keys.presets(countryKey), (old: Preset[] | undefined) => {
        if (!old) return [updatedPreset];
        return old.map((p) => (p.id === updatedPreset.id ? updatedPreset : p));
      });
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useDeletePreset(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/countries/${encodeURIComponent(countryKey)}/presets/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export interface PreviewPresetMatchingResult {
  count: number;
  listLabels?: Array<{ wiserId: number; name: string }>;
}

export function previewPresetMatchingCountQueryKey(
  countryKey: string,
  presetId: string | null,
  campaignWiserId: number | null,
) {
  const scoped = campaignWiserId != null && campaignWiserId > 0 ? campaignWiserId : null;
  return ['presets', 'preview-count', countryKey, presetId, scoped] as const;
}

export async function fetchPreviewPresetMatchingCount(
  countryKey: string,
  presetId: string,
  campaignWiserId: number,
): Promise<PreviewPresetMatchingResult> {
  const q = `?campaignWiserId=${encodeURIComponent(String(campaignWiserId))}`;
  const res = await api.get(
    `/smswiser/countries/${encodeURIComponent(countryKey)}/presets/${encodeURIComponent(presetId)}/preview-matching-count${q}`,
  );
  return unwrapApiData<PreviewPresetMatchingResult>(res.data);
}

export function usePreviewPresetMatchingCount(
  countryKey: string,
  presetId: string | null,
  /** When set, tags are computed for this campaign only — same scope as apply (avoids misleading counts). */
  campaignWiserId?: number | null,
) {
  const scopedCampaign =
    campaignWiserId != null && campaignWiserId > 0 ? campaignWiserId : null;
  return useQuery({
    queryKey: previewPresetMatchingCountQueryKey(countryKey, presetId, scopedCampaign),
    queryFn: async (): Promise<PreviewPresetMatchingResult> => {
      if (!presetId) return { count: 0 };
      if (scopedCampaign == null) {
        const res = await api.get(
          `/smswiser/countries/${encodeURIComponent(countryKey)}/presets/${presetId}/preview-matching-count`,
        );
        return unwrapApiData<PreviewPresetMatchingResult>(res.data);
      }
      return fetchPreviewPresetMatchingCount(countryKey, presetId, scopedCampaign);
    },
    enabled: Boolean(countryKey && presetId),
    staleTime: 30_000,
  });
}

export interface PresetFolderDto {
  id: string;
  label: string;
}

export function usePresetFolders(countryKey: string) {
  return useQuery({
    queryKey: keys.presetFolders(countryKey),
    queryFn: async () => {
      const res = await api.get(`/smswiser/countries/${encodeURIComponent(countryKey)}/preset-folders`);
      return unwrapApiData<PresetFolderDto[]>(res.data);
    },
    enabled: Boolean(countryKey),
    staleTime: 60_000,
  });
}

export function useCreatePresetFolder(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const res = await api.post(`/smswiser/countries/${encodeURIComponent(countryKey)}/preset-folders`, {
        label,
      });
      return unwrapApiData<PresetFolderDto>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presetFolders(countryKey) });
    },
  });
}

export function useUpdatePresetFolder(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) => {
      const res = await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/preset-folders/${encodeURIComponent(id)}`,
        { label },
      );
      return unwrapApiData<PresetFolderDto>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presetFolders(countryKey) });
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useDeletePresetFolder(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/preset-folders/${encodeURIComponent(id)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presetFolders(countryKey) });
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useReorderPresets(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedPresetIds: string[]) => {
      await api.patch(`/smswiser/countries/${encodeURIComponent(countryKey)}/presets/reorder`, {
        orderedPresetIds,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useReorderPresetsInFolder(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { folderId: string; orderedPresetIds: string[] }) => {
      await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/preset-folders/${encodeURIComponent(params.folderId)}/reorder-presets`,
        { orderedPresetIds: params.orderedPresetIds },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useBulkPresetFolders(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      presetIds: string[];
      mode: 'add' | 'replace';
      folderIds: string[];
    }) => {
      await api.patch(`/smswiser/countries/${encodeURIComponent(countryKey)}/presets/bulk-folders`, params);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.presets(countryKey) });
    },
  });
}

export function useApplyPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      presetId,
      workDate,
      campaignWiserId,
      itemCampaignWiserIdOverrides,
      smsTextsOverride,
      smsTextsPerDrop,
      aiTextMetadataPerDrop,
      senderNamesOverride,
      senderNamesPerDrop,
      routeId,
      shortLinkTldValue,
    }: {
      countryKey: string;
      presetId: string;
      workDate: string;
      campaignWiserId: number;
      itemCampaignWiserIdOverrides?: Record<string, number>;
      smsTextsOverride?: string[];
      smsTextsPerDrop?: string[][];
      aiTextMetadataPerDrop?: {
        aiTextGroupId?: string | null;
        aiTextGroupName?: string | null;
        aiTextRoleId?: string | null;
        aiTextRoleName?: string | null;
      }[];
      senderNamesOverride?: string[];
      senderNamesPerDrop?: string[][];
      routeId?: number;
      shortLinkTldValue?: string;
    }) => {
      const body: Record<string, unknown> = {
        workDate,
        campaignWiserId,
        itemCampaignWiserIdOverrides,
      };
      if (smsTextsPerDrop?.length) body.smsTextsPerDrop = smsTextsPerDrop;
      else if (smsTextsOverride?.length) body.smsTextsOverride = smsTextsOverride;
      if (aiTextMetadataPerDrop?.length) body.aiTextMetadataPerDrop = aiTextMetadataPerDrop;
      if (senderNamesPerDrop?.length) body.senderNamesPerDrop = senderNamesPerDrop;
      else if (senderNamesOverride?.length) body.senderNamesOverride = senderNamesOverride;
      if (routeId != null && routeId > 0) body.routeId = routeId;
      if (shortLinkTldValue?.trim()) body.shortLinkTldValue = shortLinkTldValue.trim();
      const res = await api.post(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/presets/${presetId}/apply`,
        body,
      );
      return unwrapApiData<{ created: number; createdIds: string[]; wiserIds: number[] }>(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsByCountryPrefix(vars.countryKey) });
    },
  });
}
